import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';

export interface ConfirmOptions {
  /** Defaults to common:confirmTitle. */
  title?: string;
  message: React.ReactNode;
  /** Defaults to common:actions.confirm. */
  confirmLabel?: string;
  /** Defaults to common:actions.cancel. */
  cancelLabel?: string;
  /** 'error' for destructive actions (delete, disconnect). Default 'primary'. */
  confirmColor?: 'primary' | 'error' | 'warning';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

interface PendingRequest {
  opts: ConfirmOptions;
  resolve: (value: boolean) => void;
}

/**
 * App-wide confirmation dialog. Provides a promise-based `confirm()` so call
 * sites read almost like the old `window.confirm`, but render the themed MUI
 * dialog instead of the browser's native prompt (project rule: confirmations
 * are always a styled modal, never window.confirm/alert).
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, message, confirmColor: 'error' }))) return;
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  const [pending, setPending] = useState<PendingRequest | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ opts, resolve })),
    [],
  );

  const settle = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialog
          open
          title={pending.opts.title ?? t('confirmTitle')}
          message={pending.opts.message}
          confirmLabel={pending.opts.confirmLabel ?? t('actions.confirm')}
          cancelLabel={pending.opts.cancelLabel ?? t('actions.cancel')}
          {...(pending.opts.confirmColor ? { confirmColor: pending.opts.confirmColor } : {})}
          onConfirm={() => settle(true)}
          onClose={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

/** Returns `confirm(opts): Promise<boolean>` — resolves true on confirm, false on cancel. */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}
